import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import deviceFlagService from '@/lib/device-flag.service';
import type { DeviceFlagInput, FlagResultsParams } from '@/lib/device-flag.service';

export const deviceFlagKeys = {
  all: ['device-flags'] as const,
  list: () => [...deviceFlagKeys.all, 'list'] as const,
  detail: (id: string) => [...deviceFlagKeys.all, 'detail', id] as const,
  results: (id: string, params: FlagResultsParams) => [...deviceFlagKeys.all, 'results', id, params] as const,
  forDevice: (deviceId: string) => [...deviceFlagKeys.all, 'device', deviceId] as const,
};

/** Every flag with its result counts. Refreshes while open: checks land in the background. */
export function useDeviceFlags(enabled = true) {
  return useQuery({
    queryKey: deviceFlagKeys.list(),
    queryFn: deviceFlagService.list,
    enabled,
    refetchInterval: 60_000,
  });
}

export function useDeviceFlag(id: string | undefined) {
  return useQuery({
    queryKey: deviceFlagKeys.detail(id ?? ''),
    queryFn: () => deviceFlagService.get(id!),
    enabled: !!id,
  });
}

export function useFlagResults(id: string, params: FlagResultsParams) {
  return useQuery({
    queryKey: deviceFlagKeys.results(id, params),
    queryFn: () => deviceFlagService.results(id, params),
    enabled: !!id,
    placeholderData: keepPreviousData,
    refetchInterval: 15_000,
  });
}

/** Flags currently flagging one machine. Pass enabled=false for non-super-admins. */
export function useDeviceFlagsForDevice(deviceId: string, enabled: boolean) {
  return useQuery({
    queryKey: deviceFlagKeys.forDevice(deviceId),
    queryFn: () => deviceFlagService.forDevice(deviceId),
    enabled: enabled && !!deviceId,
  });
}

/** Every mutation can change counts, chips and banners: refresh all flag queries. */
function useFlagMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: deviceFlagKeys.all }),
  });
}

export const useCreateFlag = () => useFlagMutation((data: DeviceFlagInput) => deviceFlagService.create(data));
export const useUpdateFlag = () =>
  useFlagMutation(({ id, data }: { id: string; data: DeviceFlagInput }) => deviceFlagService.update(id, data));
export const useDeleteFlag = () => useFlagMutation((id: string) => deviceFlagService.remove(id));
export const useTestFlag = () =>
  useFlagMutation(({ id, deviceId }: { id: string; deviceId: string }) => deviceFlagService.test(id, deviceId));
export const useEnableFlag = () => useFlagMutation((id: string) => deviceFlagService.enable(id));
export const useDisableFlag = () => useFlagMutation((id: string) => deviceFlagService.disable(id));
export const useRunFlag = () => useFlagMutation((id: string) => deviceFlagService.run(id));
export const useRecheckFlag = () =>
  useFlagMutation(({ id, deviceId }: { id: string; deviceId: string }) => deviceFlagService.recheck(id, deviceId));
